import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faClone, faPenToSquare, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { duplicateEngageMessage, refreshStats } from 'api/engage';
import Breadcrumbs from 'assets/js/pages/engage/components/Breadcrumbs';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentEngageMessage } from 'customHooks/useCurrentEngageMessage';
import { isoDateFromServer } from 'helpers/dates';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

const EngageMetricsHeader = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();
    const match = useRouteMatch();
    const getMessage = useCurrentEngageMessage();

    const { mutate: duplicateMessage } = useMutation({
        mutationFn: (messageId: number) => duplicateEngageMessage(accountId, messageId),
        onSuccess: message => {
            success('Message duplicated successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
            history.push(`/accounts/${accountId}/engage/${message.channel}/${message.id}/edit`);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to duplicate message');
        },
    });

    const { mutate: refreshMessageStats } = useMutation({
        mutationFn: (messageId: number) => refreshStats(accountId, messageId),
        onSuccess: () => {
            success('Message stats refreshed successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to refresh message stats');
        },
    });

    const message = getMessage.data;
    const sentAt = message?.sent_at ? isoDateFromServer(message.sent_at) : null;
    const dayTimeString = sentAt?.toFormat('MMM d, yyyy @ h:mm a');

    return (
        <div className="flex flex-col gap-y-4">
            <Breadcrumbs current={message.internal_name} />
            <div className="flex items-center justify-between gap-y-4">
                <div>
                    <Link
                        to={`${match.url}/edit`}
                        aria-label={`Edit message ${message.internal_name}`}
                        className={'flex items-center space-x-2 text-black hover:underline'}
                    >
                        <h1 className="text-xl font-medium">{message.internal_name}</h1>
                        <FontAwesomeIcon icon={faPenToSquare} className="text-xs" />
                    </Link>
                    <p className="text-zinc-700">{dayTimeString}</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        color="neutral"
                        variant="outline"
                        onClick={() => refreshMessageStats(message.id)}
                        aria-label="Refresh message statistics"
                    >
                        <FontAwesomeIcon icon={faRefresh} />
                        Refresh
                    </Button>
                    <Button
                        color="neutral"
                        variant="outline"
                        onClick={() => duplicateMessage(message.id)}
                        aria-label={`Duplicate message ${message.internal_name}`}
                    >
                        <FontAwesomeIcon icon={faClone} />
                        Duplicate
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { EngageMetricsHeader };
